import type {
  Attachment,
  ChannelSummary,
  MessageReceipt,
  CommsMember,
  CommsMessage,
  MessageSegment,
} from '../interfaces/ICommsApi.js';
import { handle } from './errors.js';
import type { AuthedFetch } from './transport.js';

/**
 * Path for the authenticated download route. Not directly usable as an
 * `<img src>` on the web (the session rides in a cookie or header, not the
 * URL) — the web fetches it via `authedFetch` and `URL.createObjectURL`.
 */
export function attachmentDownloadPath(siteId: string, fileId: string): string {
  return `/api/sites/${encodeURIComponent(siteId)}/comms/attachments/${encodeURIComponent(fileId)}`;
}

/**
 * The comms REST API over an injected transport — each app binds its own
 * `authedFetch` in its `apis/comms.ts`. The runtime socket is not here; it
 * lives in `../comms/`.
 */
export function createCommsApi(authedFetch: AuthedFetch) {
  async function fetchChannels(siteId: string, signal?: AbortSignal): Promise<ChannelsResponse> {
    const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/comms/channels`, { signal });
    if (!res.ok) return handle(res);
    return res.json() as Promise<ChannelsResponse>;
  }

  /**
   * Paginated history for one channel. `before` is the message id to fetch
   * older messages than (undefined = most recent page). Page size is fixed
   * server-side.
   */
  async function fetchChannelHistory(
    siteId: string,
    channelId: string,
    before: string | undefined,
    signal?: AbortSignal,
  ): Promise<HistoryResponse> {
    const params = new URLSearchParams();
    if (before) params.set('before', before);
    const url = `/api/sites/${encodeURIComponent(siteId)}/comms/channels/${encodeURIComponent(channelId)}/messages?${params}`;
    const res = await authedFetch(url, { signal });
    if (!res.ok) return handle(res);
    return res.json() as Promise<HistoryResponse>;
  }

  /**
   * Site-scoped members — everyone assigned to the site's project who
   * is also a d-orca user. Returns `{id, username}` for each user.
   */
  async function fetchMembers(
    siteId: string,
    signal?: AbortSignal,
  ): Promise<MembersResponse> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/members`,
      { signal },
    );
    if (!res.ok) return handle(res);
    return res.json() as Promise<MembersResponse>;
  }

  async function createChannel(
    siteId: string,
    body: CreateChannelBody,
  ): Promise<CreateChannelResponse> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/channels`,
      { method: 'POST', body: JSON.stringify(body) },
    );
    if (!res.ok) return handle(res);
    return res.json() as Promise<CreateChannelResponse>;
  }

  /**
   * Open (or reuse) the direct-message conversation with one other user.
   * Idempotent server-side — repeat calls return the same channel.
   */
  async function openDm(
    siteId: string,
    memberId: string,
  ): Promise<OpenDmResponse> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/dm/open`,
      { method: 'POST', body: JSON.stringify({ memberId }) },
    );
    if (!res.ok) return handle(res);
    return res.json() as Promise<OpenDmResponse>;
  }

  async function deleteMessage(
    siteId: string,
    channelId: string,
    messageId: string,
  ): Promise<{ message: CommsMessage }> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}`,
      { method: 'DELETE' },
    );
    if (!res.ok) return handle(res);
    return res.json() as Promise<{ message: CommsMessage }>;
  }

  async function editMessage(
    siteId: string,
    channelId: string,
    messageId: string,
    segments: MessageSegment[],
  ): Promise<{ message: CommsMessage }> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}`,
      { method: 'PATCH', body: JSON.stringify({ segments }) },
    );
    if (!res.ok) return handle(res);
    return res.json() as Promise<{ message: CommsMessage }>;
  }

  async function markChannelRead(siteId: string, channelId: string): Promise<void> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/channels/${encodeURIComponent(channelId)}/read`,
      { method: 'POST' },
    );
    if (!res.ok) await handle(res);
  }

  async function fetchMessageReceipts(
    siteId: string,
    channelId: string,
    messageId: string,
    signal?: AbortSignal,
  ): Promise<MessageReceiptsResponse> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(messageId)}/receipts`,
      { signal },
    );
    if (!res.ok) return handle(res);
    return res.json() as Promise<MessageReceiptsResponse>;
  }

  async function deleteChannel(
    siteId: string,
    channelId: string,
  ): Promise<{ channel: ChannelSummary }> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/channels/${encodeURIComponent(channelId)}`,
      { method: 'DELETE' },
    );
    if (!res.ok) return handle(res);
    return res.json() as Promise<{ channel: ChannelSummary }>;
  }

  async function updateChannel(
    siteId: string,
    channelId: string,
    body: UpdateChannelBody,
  ): Promise<UpdateChannelResponse> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/channels/${encodeURIComponent(channelId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    if (!res.ok) return handle(res);
    return res.json() as Promise<UpdateChannelResponse>;
  }

  // ─── Attachments ──────────────────────────────────────────────────────────

  /**
   * Upload one file to a channel. Called at send-time.
   * The backend validates (size, MIME allow-list, magic-byte sniff) and stores
   * it as `pending`, returning the resolved attachment descriptor.
   * The app builds the multipart form — a `Blob` part on the web, a
   * `{ uri, name, type }` part on mobile — with `durationMs` (voice notes
   * only) appended BEFORE the file part: the backend streams the file part
   * straight to storage as it arrives, so any metadata fields have to be
   * read first.
   */
  async function uploadAttachment(
    siteId: string,
    channelId: string,
    form: FormData,
  ): Promise<Attachment> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/channels/${encodeURIComponent(channelId)}/attachments`,
      { method: 'POST', body: form },
    );
    if (!res.ok) return handle(res);
    const body = (await res.json()) as AttachmentUploadResponse;
    return body.attachment;
  }

  /**
   * Discard a still-pending upload. Called when a send fails after some files
   * have already uploaded, to avoid leaving orphaned blobs for the reaper.
   * Fire-and-forget — 404 (already gone) and 409 (already attached) are no-ops.
   */
  async function deleteAttachment(siteId: string, fileId: string): Promise<void> {
    const res = await authedFetch(
      `/api/sites/${encodeURIComponent(siteId)}/comms/attachments/${encodeURIComponent(fileId)}`,
      { method: 'DELETE' },
    );
    if (!res.ok && res.status !== 404 && res.status !== 409) return handle(res);
  }

  return {
    fetchChannels,
    fetchChannelHistory,
    fetchMembers,
    createChannel,
    openDm,
    deleteMessage,
    editMessage,
    markChannelRead,
    fetchMessageReceipts,
    deleteChannel,
    updateChannel,
    uploadAttachment,
    deleteAttachment,
  };
}

// ─── REST request / response shapes ─────────────────────────────────────────
export interface ChannelsResponse {
  channels: ChannelSummary[];
}

export interface HistoryResponse {
  messages: CommsMessage[];
  hasMore: boolean;
}

export interface MembersResponse {
  members: CommsMember[];
}

export interface MessageReceiptsResponse {
  audience: string[];
  receipts: MessageReceipt[];
}

export interface CreateChannelBody {
  name: string;
  memberIds: string[];
}

export interface CreateChannelResponse {
  channel: ChannelSummary;
}

export interface UpdateChannelBody {
  name?: string;
  memberIds?: string[];
}

export interface UpdateChannelResponse {
  channel: ChannelSummary;
}

export interface OpenDmResponse {
  channel: ChannelSummary;
}

export interface AttachmentUploadResponse {
  attachment: Attachment;
}
